import { useEffect, useState } from "react";
import { api, apiBlob } from "../api";
import { Shell, type ScreenProps } from "./common";

/** Дата по часам устройства в виде ГГГГ-ММ-ДД — так её понимает поле ввода даты. */
const ymd = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export default function Export({ updateReady, back, state }: ScreenProps) {
  const [staff, setStaff] = useState<{ id: string; name: string }[]>([]);
  const [from, setFrom] = useState(() => { const d = new Date(); d.setDate(1); return ymd(d); });
  const [to, setTo] = useState(() => ymd(new Date()));
  const [userId, setUserId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  useEffect(() => {
    // Не загрузился список — остаётся «Все сотрудники», выгрузке это не мешает.
    api<{ users: { id: string; name: string }[] }>("/api/auth/users")
      .then((res) => setStaff([...res.users].sort((a, b) => a.name.localeCompare(b.name, "ru"))))
      .catch(() => {});
  }, []);

  async function download() {
    if (!from || !to) return setError("Укажи обе даты периода");
    if (from > to) return setError("Начало периода не может быть позже конца");
    setBusy(true); setError(null); setDone(null);
    try {
      const blob = await apiBlob(
        `/api/export/visits?from=${from}&to=${to}` + (userId ? `&userId=${userId}` : "")
      );
      // Файл отдаётся только вошедшему, поэтому обычной ссылкой его не скачать:
      // забираем сами и подсовываем браузеру как готовый.
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Визиты ${from} — ${to}.xlsx`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
      setDone("Файл скачан — он в загрузках");
    } catch (e: any) {
      setError(
        e?.status === 0 ? "Нет связи — выгрузка возможна только онлайн"
        : e?.status === 404 ? "За этот период визитов нет"
        : e?.message ?? "Не удалось выгрузить"
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Shell title="Выгрузка визитов" onBack={back} state={state} updateReady={updateReady}>
      {error && <div className="err">{error}</div>}
      {done && <div className="note" style={{ borderLeftColor: "var(--good)", background: "var(--good-soft)" }}>{done}</div>}

      <div className="block">
        <div className="blockhead"><span className="blocktitle">Визиты в Excel</span></div>
        <div className="row2">
          <div className="field">
            <label className="label" htmlFor="fr">С</label>
            <input id="fr" className="inp" type="date" value={from} max={to}
              onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div className="field">
            <label className="label" htmlFor="to">По</label>
            <input id="to" className="inp" type="date" value={to} min={from}
              onChange={(e) => setTo(e.target.value)} />
          </div>
        </div>
        <div className="field">
          <label className="label" htmlFor="us">Сотрудник</label>
          <select id="us" className="inp" value={userId} onChange={(e) => setUserId(e.target.value)}>
            <option value="">Все сотрудники</option>
            {staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <button className="btn primary" onClick={download} disabled={busy}>
          {busy ? "Собираем файл…" : "Скачать Excel"}
        </button>
      </div>

      <div className="note">
        <b>Что в файле.</b> Одна таблица: дата и время визита, сотрудник, точка и её адрес,
        контактное лицо, комментарий, договорённости, выкладка и оставленная раздатка.
        Фото в файл не попадают — их смотрят в карточке точки.
      </div>
      <div className="hint">
        В файле есть ФИО контактных лиц. В приложении они хранятся зашифрованными,
        а в файле — открытым текстом, поэтому не пересылайте его посторонним.
      </div>
    </Shell>
  );
}
